import React, { useCallback, useMemo, useRef, useState, useEffect } from "react";
import { View, StyleSheet, Pressable, TextInput, ScrollView, Image, ActivityIndicator } from "react-native";

import * as Sentry from "@sentry/react-native";
import {
  X,
  Search,
  ChevronRight,
  FileText,
  FileImage,
  File,
  Folder,
  Video,
  Link as LinkIcon,
  AlertTriangle,
  Check,
  ArrowRight,
} from "lucide-react";
import { ConnectorType, ExternalItemType } from "@axio/graphql/generated/graphql";
import { colors } from "../../../../styles/design-system";
import CustomModal from "../CustomModal/CustomModal";
import CustomText from "../CustomText/CustomText";
import INTEGRATIONS_ICONS from "../../../../assets/integrations";
import { PickerSource } from "./ContentPicker.types";
import type { ExternalPickedItem, PickerContext } from "./ContentPicker.types";
import { useExternalBrowser, ExternalBrowserItem } from "../../../../hooks/contentPicker/useExternalBrowser";
import { useContentPickerSources } from "../../../../hooks/contentPicker/useContentPickerSources";
import { useTranslation } from "react-i18next";
import { useOAuthPopup } from "../../../../hooks/oauth/useOAuthPopup";
import { useToast } from "../../../../contexts/Toast/ToastProvider";

type TranslateFn = (key: string, options?: Record<string, unknown>) => string;

interface ContentPickerModalProps {
  visible: boolean;
  onClose: () => void;
  initialConnectorType?: ConnectorType | null;
  multiSelect?: boolean;
  onImport: (items: ExternalPickedItem[]) => void;
  context?: PickerContext;
  allowedMimeTypes?: string[];
  allowedFileExtensions?: string[];
}

function formatBytes(bytes?: number | null): string {
  if (bytes == null) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDate(iso?: string): string {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-CA");
  } catch {
    return iso;
  }
}

function getFileKindLabel(item: ExternalBrowserItem, t: TranslateFn): string {
  if (item.isFolder) return t("text.content_picker_modal_type_folder");
  if (item.mimeType?.includes("pdf")) return "PDF";
  if (item.mimeType?.includes("word") || item.mimeType?.includes("document")) return "DOC";
  if (item.mimeType?.includes("presentation")) return "PPT";
  if (item.mimeType?.includes("sheet") || item.mimeType?.includes("excel")) return "XLS";
  if (item.mimeType?.includes("image")) return t("text.content_picker_modal_type_image");
  if (item.type === ExternalItemType.Video || item.mimeType?.includes("video"))
    return t("text.content_picker_modal_type_video");
  return t("text.content_picker_modal_type_file");
}

function getPreviewAccent(item: ExternalBrowserItem): { backgroundColor: string; iconColor: string } {
  if (item.mimeType?.includes("pdf")) {
    return { backgroundColor: "#FDECEC", iconColor: "#DC2626" };
  }
  if (item.mimeType?.includes("image")) {
    return { backgroundColor: "#EEF7FF", iconColor: "#1D4ED8" };
  }
  if (item.mimeType?.includes("word") || item.mimeType?.includes("document")) {
    return { backgroundColor: "#EEF4FF", iconColor: "#2563EB" };
  }
  if (item.type === ExternalItemType.Video || item.mimeType?.includes("video")) {
    return { backgroundColor: "#F3E8FF", iconColor: "#7C3AED" };
  }
  return { backgroundColor: colors.grey[100], iconColor: colors.grey[500] };
}

function getPreviewPages(item: ExternalBrowserItem): string | null {
  if (!item.providerMetadata || typeof item.providerMetadata !== "object") {
    return null;
  }

  const maybePageCount = (item.providerMetadata as Record<string, unknown>).pageCount;
  if (typeof maybePageCount === "number") {
    return String(maybePageCount);
  }

  if (typeof maybePageCount === "string") {
    return maybePageCount;
  }

  return null;
}

function formatAllowedFileTypesLabel(extensions: string[]): string {
  const normalized = Array.from(new Set(extensions.map((extension) => extension.toUpperCase())));
  if (normalized.length === 0) return "supported file types";
  if (normalized.length === 1) return normalized[0];
  if (normalized.length === 2) return `${normalized[0]} or ${normalized[1]}`;
  return `${normalized.slice(0, -1).join(", ")}, or ${normalized[normalized.length - 1]}`;
}

function FileTypeIcon({ item }: { item: ExternalBrowserItem }) {
  if (item.isFolder) return <Folder size={18} color="#C45A16" />;
  if (item.type === ExternalItemType.Video || item.mimeType?.includes("video"))
    return <Video size={18} color="#7C3AED" />;
  if (item.mimeType?.includes("pdf")) return <FileText size={18} color="#DC2626" />;
  if (item.mimeType?.includes("word") || item.mimeType?.includes("document")) {
    return <FileText size={18} color="#2563EB" />;
  }
  if (item.mimeType?.includes("image")) return <FileImage size={18} color="#0F766E" />;
  return <File size={18} color={colors.grey[400]} />;
}

function PreviewArt({ item }: { item: ExternalBrowserItem }) {
  if (item.thumbnailUrl) {
    return <Image source={{ uri: item.thumbnailUrl }} style={styles.previewImage} resizeMode="contain" />;
  }

  const accent = getPreviewAccent(item);
  const label = item.mimeType?.includes("pdf")
    ? "PDF"
    : item.mimeType?.includes("word") || item.mimeType?.includes("document")
      ? "DOC"
      : item.mimeType?.includes("image")
        ? "content_picker_modal_type_image"
        : item.type === ExternalItemType.Video || item.mimeType?.includes("video")
          ? "content_picker_modal_type_video"
          : "content_picker_modal_type_file";

  return (
    <View style={[styles.previewArtFallback, { backgroundColor: accent.backgroundColor }]}>
      <View style={styles.previewArtIconWrap}>
        <FileTypeIcon item={item} />
      </View>
      <CustomText
        text={label}
        useTranslationText={
          !(item.mimeType?.includes("pdf") || item.mimeType?.includes("word") || item.mimeType?.includes("document"))
        }
        style={[styles.previewArtLabel, { color: accent.iconColor }]}
      />
      <CustomText
        text={"content_picker_modal_preview_caption"}
        useTranslationText={true}
        style={[styles.previewArtCaption, { color: accent.iconColor }]}
      />
    </View>
  );
}

function SourceStatusBadge({ status }: { status: PickerSource["status"] }) {
  if (status === "reauth_required") {
    return (
      <View style={badgeStyles.warning}>
        <AlertTriangle size={10} color="#D97706" />
      </View>
    );
  }

  if (status === "error") {
    return (
      <View style={badgeStyles.error}>
        <AlertTriangle size={10} color="#DC2626" />
      </View>
    );
  }

  if (status === "not_connected") {
    return <LinkIcon size={12} color={colors.grey[400]} />;
  }

  return null;
}

const badgeStyles = StyleSheet.create({
  warning: {
    marginLeft: 4,
    backgroundColor: "#FEF3C7",
    borderRadius: 6,
    padding: 2,
  },
  error: {
    marginLeft: 4,
    backgroundColor: "#FEE2E2",
    borderRadius: 6,
    padding: 2,
  },
});

export const ContentPickerModal: React.FC<ContentPickerModalProps> = ({
  visible,
  onClose,
  initialConnectorType,
  multiSelect = true,
  onImport,
  context,
  allowedMimeTypes,
  allowedFileExtensions,
}) => {
  const { t } = useTranslation();
  const { sources, loading: sourcesLoading, refetchSources } = useContentPickerSources(context);

  const { showToast } = useToast();
  const [activeConnector, setActiveConnector] = useState<ConnectorType | null>(initialConnectorType ?? null);
  const [isClearSelectionHovered, setIsClearSelectionHovered] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [previewItemId, setPreviewItemId] = useState<string | null>(null);
  const [focusedItemIndex, setFocusedItemIndex] = useState<number>(-1);
  const [focusedSidebarIndex, setFocusedSidebarIndex] = useState<number>(-1);
  const searchInputRef = useRef(null);

  useEffect(() => {
    if (initialConnectorType && visible) {
      setActiveConnector(initialConnectorType);
      setSelectedIds(new Set());
      setPreviewItemId(null);
      setFocusedItemIndex(-1);
    }
  }, [initialConnectorType, visible]);

  useEffect(() => {
    if (!activeConnector && sources.length > 0 && visible) {
      const first = sources.find((source) => source.status === "connected") ?? sources[0];
      if (first) setActiveConnector(first.connectorType);
    }
  }, [sources, activeConnector, visible]);

  const browser = useExternalBrowser(activeConnector, context?.allowedItemTypes);

  const {
    openOAuthPopup,
    loading: oauthLoading,
    reset,
  } = useOAuthPopup({
    onSuccess: () => {
      refetchSources();
      browser.refetch();
    },
  });

  const normalizedAllowedMimeTypes = useMemo(
    () => (allowedMimeTypes ?? []).map((mimeType) => mimeType.toLowerCase().trim()),
    [allowedMimeTypes]
  );
  const normalizedAllowedExtensions = useMemo(
    () => (allowedFileExtensions ?? []).map((extension) => extension.toLowerCase().trim().replace(/^\./, "")),
    [allowedFileExtensions]
  );
  const disallowedSelectionLabel = useMemo(() => {
    const allowedFileTypes = formatAllowedFileTypesLabel(normalizedAllowedExtensions);
    return `Selection unavailable for this file type. Only ${allowedFileTypes} files can be selected.`;
  }, [normalizedAllowedExtensions]);

  const isExternalItemAllowed = useCallback(
    (item: ExternalBrowserItem): boolean => {
      if (item.isFolder) return true;
      if (normalizedAllowedMimeTypes.length === 0 && normalizedAllowedExtensions.length === 0) return true;

      const mimeType = item.mimeType?.toLowerCase().trim();
      if (mimeType && normalizedAllowedMimeTypes.includes(mimeType)) {
        return true;
      }

      const extension = item.name.toLowerCase().split(".").pop() ?? "";
      if (extension && normalizedAllowedExtensions.includes(extension)) {
        return true;
      }

      return false;
    },
    [normalizedAllowedExtensions, normalizedAllowedMimeTypes]
  );

  useEffect(() => {
    reset();
  }, [activeConnector, reset]);

  useEffect(() => {
    if (browser.items.length === 0) {
      setPreviewItemId(null);
      return;
    }

    const matchingPreviewItem = previewItemId ? browser.items.find((item) => item.id === previewItemId) : null;
    if (matchingPreviewItem) {
      return;
    }

    const firstSelectedItem = browser.items.find((item) => selectedIds.has(item.id) && !item.isFolder);
    if (firstSelectedItem) {
      setPreviewItemId(firstSelectedItem.id);
    }
  }, [browser.items, previewItemId, selectedIds]);

  useEffect(() => {
    setSelectedIds((prev) => {
      const next = new Set(
        Array.from(prev).filter((id) => {
          const item = browser.items.find((candidate) => candidate.id === id);
          return item ? isExternalItemAllowed(item) : true;
        })
      );
      return next.size === prev.size ? prev : next;
    });
  }, [browser.items, isExternalItemAllowed]);

  const toggleItem = useCallback(
    (id: string) => {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        if (next.has(id)) {
          next.delete(id);
        } else {
          if (!multiSelect) next.clear();
          next.add(id);
        }
        return next;
      });
      setPreviewItemId(id);
    },
    [multiSelect]
  );

  const handleImport = useCallback(() => {
    const picked: ExternalPickedItem[] = browser.items
      .filter((item) => selectedIds.has(item.id) && !item.isFolder)
      .map((item) => ({
        kind: "external",
        connectorType: activeConnector!,
        externalId: item.id,
        name: item.name,
        mimeType: item.mimeType ?? undefined,
        url: item.url,
        sizeBytes: item.sizeBytes ?? undefined,
        thumbnailUrl: item.thumbnailUrl ?? undefined,
      }));
    onImport(picked);
    onClose();
  }, [activeConnector, browser.items, onClose, onImport, selectedIds]);

  const selectedCount = selectedIds.size;

  const handleFileListKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (browser.items.length === 0) return;

      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          setFocusedItemIndex((prev) => Math.min(prev + 1, browser.items.length - 1));
          break;
        case "ArrowUp":
          e.preventDefault();
          setFocusedItemIndex((prev) => Math.max(prev - 1, 0));
          break;
        case "Enter":
        case " ":
          e.preventDefault();
          if (focusedItemIndex >= 0 && focusedItemIndex < browser.items.length) {
            const item = browser.items[focusedItemIndex];
            if (item.isFolder) {
              browser.navigateInto(item);
              setPreviewItemId(null);
              setFocusedItemIndex(-1);
            } else {
              setPreviewItemId(item.id);
            }
          }
          break;
        case "Escape":
          if (browser.searchQuery) {
            e.preventDefault();
            browser.clearSearch();
            searchInputRef.current?.focus();
          }
          break;
      }
    },
    [browser, focusedItemIndex]
  );

  useEffect(() => {
    if (browser.items.length > 0 && focusedItemIndex === -1) {
      setFocusedItemIndex(0);
    }
  }, [browser.items, focusedItemIndex]);

  const connectedSources = useMemo(() => sources.filter((source) => source.status !== "not_connected"), [sources]);
  const connectSources = useMemo(() => sources.filter((source) => source.status === "not_connected"), [sources]);
  const activeSource = sources.find((source) => source.connectorType === activeConnector);
  const previewItem = browser.items.find((item) => item.id === previewItemId && !item.isFolder) ?? null;

  const handleOAuthConnect = useCallback(
    async (source: PickerSource) => {
      try {
        if (!source.authUrl) {
          showToast({
            message: `Unable to start connection for ${source.connectorType || "unknown"}`,
            type: "error",
          });
          return;
        }

        const result = await openOAuthPopup(source.authUrl, source.connectorType);

        if (result) {
          // Switch the active connector to the newly connected source so the
          // file browser loads immediately after refetch completes.
          setActiveConnector(source.connectorType);
          await refetchSources();
        }
      } catch (error) {
        Sentry.captureException(error);
        showToast({
          message: `Unable to start connection for ${source.connectorType || "unknown"}`,
          type: "error",
        });
      }
    },
    [openOAuthPopup, refetchSources, showToast]
  );

  const renderItem = useCallback(
    ({ item, index }: { item: ExternalBrowserItem; index: number }) => {
      const isSelected = selectedIds.has(item.id);
      const isFocused = focusedItemIndex === index;
      const isPreviewed = previewItemId === item.id && !item.isFolder;
      const isSelectable = !item.isFolder && isExternalItemAllowed(item);

      return (
        <Pressable
          key={item.id}
          role="option"
          aria-selected={isSelected}
          accessibilityState={{ selected: isSelected }}
          accessibilityLabel={item.name}
          style={({ hovered }) => [
            styles.fileRow,
            hovered && styles.fileRowHovered,
            isSelected && styles.fileRowSelected,
            isPreviewed && styles.fileRowPreviewed,
            isFocused && styles.fileRowFocused,
          ]}
          onPress={() => {
            setFocusedItemIndex(index);
            if (item.isFolder) {
              browser.navigateInto(item);
              setPreviewItemId(null);
              return;
            }

            setPreviewItemId(item.id);
          }}>
          <Pressable
            onPress={() => {
              setFocusedItemIndex(index);
              if (isSelectable) {
                toggleItem(item.id);
              }
            }}
            disabled={!isSelectable}
            accessibilityLabel={
              item.isFolder ? item.name : t("text.content_picker_modal_select_item", { name: item.name })
            }
            style={styles.checkboxHitSlop}>
            <View style={[styles.checkboxBox, isSelected && styles.checkboxBoxActive]}>
              {isSelected && <Check size={11} color={colors.brand[500]} strokeWidth={3} />}
            </View>
          </Pressable>

          <View style={styles.fileIconWrap}>
            <FileTypeIcon item={item} />
          </View>

          <CustomText text={item.name} useTranslationText={false} style={styles.fileName} numberOfLines={1} />
          <CustomText
            text={item.isFolder ? " " : formatBytes(item.sizeBytes)}
            useTranslationText={false}
            style={styles.fileMeta}
          />
          <CustomText text={formatDate(item.updatedAt)} useTranslationText={false} style={styles.fileDate} />
          {item.isFolder && <ChevronRight size={14} color={colors.grey[400]} />}
        </Pressable>
      );
    },
    [browser, focusedItemIndex, isExternalItemAllowed, previewItemId, selectedIds, t, toggleItem]
  );

  const getSourceIcon = (source: PickerSource) => {
    const iconValue = INTEGRATIONS_ICONS[source.iconKey as keyof typeof INTEGRATIONS_ICONS];
    if (iconValue) {
      return <Image source={{ uri: iconValue }} style={styles.sidebarSourceIcon} resizeMode="contain" />;
    }

    return <File size={18} color={colors.grey[500]} />;
  };

  const renderMainContent = () => {
    if (!activeConnector) {
      return (
        <View style={styles.statePane}>
          <CustomText
            text={"content_picker_modal_select_source_prompt"}
            useTranslationText={true}
            style={styles.stateText}
          />
        </View>
      );
    }

    if (activeSource?.status === "not_connected") {
      const iconValue = INTEGRATIONS_ICONS[activeSource.iconKey as keyof typeof INTEGRATIONS_ICONS];
      return (
        <View style={styles.statePane}>
          <View style={styles.notConnectedCard}>
            <View style={styles.notConnectedIconCircle}>
              {iconValue ? (
                <Image source={{ uri: iconValue }} style={{ width: 32, height: 32 }} resizeMode="contain" />
              ) : (
                <LinkIcon size={24} color={colors.brand[500]} />
              )}
            </View>
            <CustomText
              text={"content_picker_modal_connect_title"}
              interpolation={{ source: activeSource.label }}
              useTranslationText={true}
              style={styles.notConnectedTitle}
            />
            <CustomText
              text={"content_picker_modal_connect_description"}
              interpolation={{ source: activeSource.label }}
              useTranslationText={true}
              style={styles.notConnectedDesc}
            />
            <Pressable
              style={styles.connectButton}
              onPress={async () => {
                if (!activeSource) return;
                await handleOAuthConnect(activeSource);
              }}
              disabled={oauthLoading}>
              <CustomText
                text={"content_picker_connect_source"}
                interpolation={{ source: activeSource.label }}
                useTranslationText={true}
                style={styles.connectButtonText}
              />
              <ArrowRight size={16} color={colors.white} />
            </Pressable>
            <CustomText
              text={"content_picker_modal_connect_footer"}
              useTranslationText={true}
              style={styles.notConnectedFooter}
            />
          </View>
        </View>
      );
    }

    if (activeSource?.status === "reauth_required") {
      return (
        <View style={styles.statePane}>
          <AlertTriangle size={32} color="#D97706" />
          <CustomText
            text={"content_picker_modal_reauth_required"}
            interpolation={{ source: activeSource.label }}
            useTranslationText={true}
            style={styles.stateText}
          />
          <Pressable
            style={styles.connectButton}
            onPress={async () => {
              await handleOAuthConnect(activeSource);
            }}
            disabled={oauthLoading}>
            <CustomText
              text={"content_picker_connect_source"}
              interpolation={{ source: activeSource.label }}
              useTranslationText={true}
              style={styles.connectButtonText}
            />
            <ArrowRight size={16} color={colors.white} />
          </Pressable>
        </View>
      );
    }

    if (browser.error) {
      return (
        <View style={styles.statePane}>
          <AlertTriangle size={32} color={colors.error[500]} />
          <CustomText
            text={"content_picker_modal_error_loading"}
            useTranslationText={true}
            style={[styles.stateText, styles.errorText]}
          />
        </View>
      );
    }

    if (browser.loading && browser.items.length === 0) {
      return (
        <View style={styles.statePane}>
          <ActivityIndicator size="large" color={colors.brand[500]} />
        </View>
      );
    }

    if (!browser.loading && browser.items.length === 0) {
      return (
        <View style={styles.statePane}>
          <File size={32} color={colors.grey[300]} />
          <CustomText
            text={browser.searchQuery ? "content_picker_modal_no_search_results" : "content_picker_modal_empty_folder"}
            useTranslationText={true}
            style={styles.stateText}
          />
        </View>
      );
    }

    return (
      <ScrollView
        style={styles.fileList}
        contentContainerStyle={styles.fileListContent}
        role="listbox"
        aria-label="File list"
        accessibilityRole="listbox"
        aria-multiselectable={multiSelect}
        // @ts-expect-error tabIndex and onKeyDown are valid on web
        tabIndex={0}
        onKeyDown={handleFileListKeyDown}>
        {browser.items.map((item, index) => renderItem({ item, index }))}
        {browser.hasNextPage && (
          <Pressable style={styles.loadMoreBtn} onPress={browser.loadMore}>
            <CustomText
              text={browser.loading ? "content_picker_modal_loading" : "content_picker_modal_load_more"}
              useTranslationText={true}
              style={styles.loadMoreText}
            />
          </Pressable>
        )}
      </ScrollView>
    );
  };

  const renderPreviewPane = () => {
    if (!previewItem) {
      return (
        <View style={styles.previewEmpty}>
          <CustomText
            text={"content_picker_modal_preview_empty"}
            useTranslationText={true}
            style={styles.previewEmptyText}
          />
        </View>
      );
    }

    const pages = getPreviewPages(previewItem);
    const isSelected = selectedIds.has(previewItem.id);
    const isSelectable = isExternalItemAllowed(previewItem);

    return (
      <View style={styles.previewPane}>
        <PreviewArt item={previewItem} />

        <CustomText text={previewItem.name} useTranslationText={false} style={styles.previewFileName} />

        <View style={styles.previewMetadataList}>
          <View style={styles.previewMetadataRow}>
            <CustomText text={"content_picker_modal_meta_type"} useTranslationText={true} style={styles.metaLabel} />
            <CustomText text={getFileKindLabel(previewItem, t)} useTranslationText={false} style={styles.metaValue} />
          </View>
          <View style={styles.previewMetadataRow}>
            <CustomText text={"content_picker_modal_meta_size"} useTranslationText={true} style={styles.metaLabel} />
            <CustomText text={formatBytes(previewItem.sizeBytes)} useTranslationText={false} style={styles.metaValue} />
          </View>
          <View style={styles.previewMetadataRow}>
            <CustomText
              text={"content_picker_modal_meta_modified"}
              useTranslationText={true}
              style={styles.metaLabel}
            />
            <CustomText text={formatDate(previewItem.updatedAt)} useTranslationText={false} style={styles.metaValue} />
          </View>
          {pages ? (
            <View style={styles.previewMetadataRow}>
              <CustomText text={"content_picker_modal_meta_pages"} useTranslationText={true} style={styles.metaLabel} />
              <CustomText text={pages} useTranslationText={false} style={styles.metaValue} />
            </View>
          ) : null}
        </View>

        <Pressable
          style={[styles.previewSelectBtn, isSelected && styles.previewSelectBtnActive]}
          onPress={() => {
            if (isSelectable) {
              toggleItem(previewItem.id);
            }
          }}
          disabled={!isSelectable}
          accessibilityRole="button"
          accessibilityLabel={
            isSelectable
              ? isSelected
                ? t("text.content_picker_modal_remove_from_selection")
                : t("text.content_picker_modal_add_to_selection")
              : disallowedSelectionLabel
          }>
          <CustomText
            text={
              isSelectable
                ? isSelected
                  ? "content_picker_modal_remove_from_selection"
                  : "content_picker_modal_add_to_selection"
                : disallowedSelectionLabel.replace("Selection unavailable for this file type. ", "")
            }
            useTranslationText={isSelectable}
            style={styles.previewSelectBtnText}
          />
        </Pressable>
      </View>
    );
  };

  return (
    <CustomModal
      visible={visible}
      onRequestClose={onClose}
      style={styles.modal}
      title={
        activeConnector
          ? t("text.content_picker_modal_title_source", {
              source: activeSource?.label ?? t("text.content_picker_modal_title_fallback_source"),
            })
          : t("text.content_picker_modal_title")
      }
      showCloseButton
      ariaLabel="Content Picker Modal">
      <View style={styles.modalBody}>
        <View style={styles.sidebar} role="navigation" aria-label="Sources">
          <CustomText
            text={"content_picker_modal_sources_label"}
            useTranslationText={true}
            style={styles.sidebarLabel}
          />

          {sourcesLoading ? (
            <ActivityIndicator size="small" color={colors.brand[500]} style={styles.sourcesLoading} />
          ) : (
            <>
              {connectedSources.map((source, index) => {
                const isActive = activeConnector === source.connectorType;
                const isFocused = focusedSidebarIndex === index;
                return (
                  <Pressable
                    key={source.connectorType}
                    role="button"
                    aria-pressed={isActive}
                    accessibilityRole="button"
                    accessibilityLabel={source.label}
                    style={[
                      styles.sidebarItem,
                      isActive && styles.sidebarItemActive,
                      isFocused && styles.sidebarItemFocused,
                    ]}
                    onPress={() => {
                      setActiveConnector(source.connectorType);
                      setSelectedIds(new Set());
                      setPreviewItemId(null);
                      setFocusedSidebarIndex(index);
                    }}
                    onFocus={() => setFocusedSidebarIndex(index)}
                    onBlur={() => setFocusedSidebarIndex(-1)}>
                    {getSourceIcon(source)}
                    <CustomText
                      text={source.label}
                      useTranslationText={false}
                      style={[styles.sidebarItemText, isActive && styles.sidebarItemTextActive]}
                      numberOfLines={1}
                    />
                    <SourceStatusBadge status={source.status} />
                  </Pressable>
                );
              })}

              {connectSources.length > 0 ? (
                <View style={styles.connectSection}>
                  <CustomText
                    text={"content_picker_modal_connect_label"}
                    useTranslationText={true}
                    style={styles.connectLabel}
                  />
                  {connectSources.map((source, index) => {
                    const sidebarIndex = connectedSources.length + index;
                    return (
                      <Pressable
                        key={source.connectorType}
                        accessibilityRole="button"
                        accessibilityLabel={source.label}
                        style={styles.sidebarItem}
                        onPress={() => {
                          setActiveConnector(source.connectorType);
                          setPreviewItemId(null);
                          setFocusedSidebarIndex(sidebarIndex);
                        }}
                        onFocus={() => setFocusedSidebarIndex(sidebarIndex)}
                        onBlur={() => setFocusedSidebarIndex(-1)}>
                        {getSourceIcon(source)}
                        <CustomText text={source.label} useTranslationText={false} style={styles.sidebarItemText} />
                        <SourceStatusBadge status={source.status} />
                      </Pressable>
                    );
                  })}
                </View>
              ) : null}
            </>
          )}
        </View>

        <View style={styles.mainPane}>
          <View style={styles.breadcrumbs} role="navigation" aria-label="File path">
            {browser.breadcrumbs.map((crumb, index) => (
              <React.Fragment key={`${crumb.containerId ?? "root"}-${index}`}>
                {index > 0 && <ChevronRight size={12} color={colors.grey[400]} />}
                <Pressable
                  onPress={() => browser.navigateToBreadcrumb(index)}
                  disabled={index === browser.breadcrumbs.length - 1}>
                  <CustomText
                    text={index === 0 ? (activeSource?.label ?? "content_picker_modal_root_breadcrumb") : crumb.label}
                    useTranslationText={index === 0 && !activeSource}
                    style={[
                      styles.breadcrumbText,
                      index === browser.breadcrumbs.length - 1 && styles.breadcrumbTextActive,
                    ]}
                  />
                </Pressable>
              </React.Fragment>
            ))}
          </View>

          <View style={styles.searchRow}>
            <Search size={16} color={colors.grey[400]} />
            <TextInput
              ref={searchInputRef}
              placeholder={t("text.content_picker_modal_search_placeholder_source", {
                source: activeSource?.label ?? t("text.content_picker_modal_title_fallback_source"),
              })}
              placeholderTextColor={colors.grey[400]}
              value={browser.searchQuery}
              onChangeText={browser.setSearch}
              style={styles.searchInput}
              accessibilityLabel={t("text.content_picker_modal_search_label")}
              // @ts-expect-error outlineStyle is valid on RN Web
              outlineStyle="none"
            />
            {browser.searchQuery ? (
              <Pressable onPress={browser.clearSearch} accessibilityLabel={t("text.content_picker_modal_clear_search")}>
                <X size={14} color={colors.grey[400]} />
              </Pressable>
            ) : null}
          </View>

          {renderMainContent()}
        </View>

        <View style={styles.previewRail}>{renderPreviewPane()}</View>
      </View>

      <View style={styles.footer}>
        <View style={styles.footerLeft}>
          <CustomText
            text={
              selectedCount > 0
                ? `content_picker_modal_selected_files_${selectedCount === 1 ? "one" : "other"}`
                : "content_picker_modal_no_files_selected"
            }
            interpolation={selectedCount > 0 ? { count: selectedCount } : {}}
            useTranslationText={true}
            style={styles.footerCountText}
          />
          {selectedCount > 0 ? (
            <Pressable
              onPress={() => setSelectedIds(new Set())}
              onHoverIn={() => setIsClearSelectionHovered(true)}
              onHoverOut={() => setIsClearSelectionHovered(false)}
              accessibilityRole="button"
              accessibilityLabel={t("text.content_picker_modal_clear_selection")}>
              <CustomText
                text={"content_picker_modal_clear_selection"}
                useTranslationText={true}
                style={[styles.clearBtn, isClearSelectionHovered && styles.clearBtnHover]}
              />
            </Pressable>
          ) : null}
        </View>

        <Pressable
          style={[styles.importBtn, selectedCount === 0 && styles.importBtnDisabled]}
          disabled={selectedCount === 0}
          onPress={handleImport}
          accessibilityLabel={
            selectedCount > 0
              ? t(`text.content_picker_modal_import_label_${selectedCount === 1 ? "one" : "other"}`, {
                  count: selectedCount,
                })
              : t("text.content_picker_modal_import_label_empty")
          }
          accessibilityRole="button">
          <CustomText
            text={
              selectedCount > 0
                ? `content_picker_modal_import_btn_${selectedCount === 1 ? "one" : "other"}`
                : "content_picker_modal_import_btn_empty"
            }
            interpolation={selectedCount > 0 ? { count: selectedCount } : {}}
            useTranslationText={true}
            style={styles.importBtnText}
          />
          <ArrowRight size={14} color={colors.white} />
        </Pressable>
      </View>
    </CustomModal>
  );
};

const styles = StyleSheet.create({
  modal: {
    maxWidth: 1200,
    width: "90%",
  },

  modalBody: {
    flexDirection: "row",
    height: 480,
    gap: 2,
  },
  sidebar: {
    width: 200,
    borderRightWidth: 1,
    borderRightColor: colors.grey[200],
    paddingHorizontal: 16,
    paddingVertical: 20,
    backgroundColor: colors.grey[50],
  },
  sidebarLabel: {
    fontSize: 11,
    lineHeight: 18,
    fontWeight: "700",
    color: colors.grey[500],
    letterSpacing: 1.2,
    textTransform: "uppercase",
    marginBottom: 10,
  } as any,
  sourcesLoading: {
    marginTop: 8,
  },
  sidebarItem: {
    minHeight: 44,
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
    paddingHorizontal: 14,
    paddingVertical: 10,
    borderRadius: 999,
  },
  sidebarItemActive: {
    backgroundColor: colors.white,
    borderWidth: 1,
    borderColor: colors.grey[200],
    shadowColor: "#101828",
    shadowOpacity: 0.08,
    shadowRadius: 10,
    shadowOffset: { width: 0, height: 4 },
  },
  sidebarItemFocused: {
    outlineWidth: 2,
    outlineStyle: "solid",
    outlineColor: colors.brand[300],
  } as any,
  sidebarSourceIcon: {
    width: 24,
    height: 24,
  },
  sidebarItemText: {
    flex: 1,
    fontSize: 15,
    color: colors.grey[600],
  },
  sidebarItemTextActive: {
    color: colors.grey[900],
    fontWeight: "500",
  },
  connectSection: {
    marginTop: 16,
    paddingTop: 14,
    borderTopWidth: 1,
    borderTopColor: colors.grey[200],
    gap: 6,
  },
  connectLabel: {
    fontSize: 13,
    color: colors.grey[500],
    marginBottom: 4,
  },
  mainPane: {
    flex: 1,
    paddingHorizontal: 20,
    paddingTop: 20,
    paddingBottom: 0,
    borderRightWidth: 1,
    borderRightColor: colors.grey[200],
  },
  breadcrumbs: {
    flexDirection: "row",
    alignItems: "center",
    gap: 10,
    flexWrap: "wrap",
    marginBottom: 16,
  },
  breadcrumbText: {
    fontSize: 15,
    color: colors.grey[500],
  },
  breadcrumbTextActive: {
    color: colors.grey[900],
    fontWeight: "500",
  },
  searchRow: {
    minHeight: 48,
    flexDirection: "row",
    alignItems: "center",
    gap: 10,
    borderWidth: 1,
    borderColor: colors.grey[300],
    borderRadius: 12,
    paddingHorizontal: 14,
    paddingVertical: 10,
    marginBottom: 16,
  },
  searchInput: {
    flex: 1,
    fontSize: 14,
    color: colors.grey[900],
  } as any,
  fileList: {
    flex: 1,
  },
  fileListContent: {
    paddingBottom: 16,
  },
  fileRow: {
    minHeight: 52,
    flexDirection: "row",
    alignItems: "center",
    gap: 10,
    paddingHorizontal: 10,
    paddingVertical: 8,
    borderRadius: 20,
    marginBottom: 4,
  },
  fileRowHovered: {
    backgroundColor: colors.grey[200],
  },

  fileRowSelected: {
    backgroundColor: "#DDE5FF",
  },
  fileRowPreviewed: {
    backgroundColor: "#EEF2FF",
  },
  fileRowFocused: {
    outlineWidth: 2,
    outlineStyle: "solid",
    outlineColor: colors.brand[300],
    outlineOffset: -2,
  } as any,
  checkboxHitSlop: {
    padding: 4,
  },
  checkboxBox: {
    width: 14,
    height: 14,
    borderRadius: 4,
    borderWidth: 1,
    borderColor: colors.grey[300],
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: colors.white,
  },
  checkboxBoxActive: {
    backgroundColor: colors.white,
    borderColor: colors.white,
  },
  fileIconWrap: {
    width: 20,
    alignItems: "center",
  },
  fileName: {
    flex: 1,
    fontSize: 14,
    color: colors.grey[900],
  },
  fileMeta: {
    width: 72,
    fontSize: 12,
    color: colors.grey[500],
    textAlign: "right",
  },
  fileDate: {
    width: 92,
    fontSize: 12,
    color: colors.grey[500],
    textAlign: "right",
  },
  loadMoreBtn: {
    alignItems: "center",
    paddingVertical: 14,
  },
  loadMoreText: {
    fontSize: 13,
    color: colors.brand[600],
    fontWeight: "500",
  },
  statePane: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    gap: 12,
    paddingHorizontal: 24,
  },
  stateText: {
    fontSize: 14,
    color: colors.grey[500],
    textAlign: "center",
    maxWidth: 280,
  },
  errorText: {
    color: colors.error[500],
  },
  notConnectedCard: {
    backgroundColor: "#F8F9FA",
    borderRadius: 16,
    padding: 32,
    alignItems: "center",
    maxWidth: 420,
    width: "100%",
    borderWidth: 1,
    borderColor: colors.grey[100],
  },
  notConnectedIconCircle: {
    width: 64,
    height: 64,
    borderRadius: 32,
    backgroundColor: colors.white,
    alignItems: "center",
    justifyContent: "center",
    marginBottom: 24,
    shadowColor: "#101828",
    shadowOpacity: 0.08,
    shadowRadius: 10,
    shadowOffset: { width: 0, height: 4 },
  },
  notConnectedTitle: {
    fontSize: 18,
    fontWeight: "600",
    color: colors.grey[900],
    marginBottom: 8,
    textAlign: "center",
  },
  notConnectedDesc: {
    fontSize: 14,
    color: colors.grey[500],
    textAlign: "center",
    marginBottom: 24,
    lineHeight: 20,
  },
  connectButton: {
    backgroundColor: colors.brand[500],
    borderRadius: 8,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    paddingVertical: 12,
    paddingHorizontal: 24,
    width: "100%",
    marginBottom: 20,
    gap: 8,
  },
  connectButtonText: {
    fontSize: 14,
    fontWeight: "600",
    color: colors.white,
  },
  notConnectedFooter: {
    fontSize: 12,
    color: colors.grey[400],
    textAlign: "center",
  },
  previewRail: {
    width: 300,
    paddingHorizontal: 20,
    paddingVertical: 20,
    backgroundColor: colors.white,
  },
  previewPane: {
    flex: 1,
  },
  previewEmpty: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    paddingHorizontal: 24,
  },
  previewEmptyText: {
    fontSize: 16,
    color: "#61708B",
    textAlign: "center",
  },
  previewArtFallback: {
    height: 180,
    borderRadius: 22,
    alignItems: "center",
    justifyContent: "center",
    marginBottom: 22,
  },
  previewArtIconWrap: {
    marginBottom: 10,
  },
  previewArtLabel: {
    fontSize: 18,
    fontWeight: "500",
    marginBottom: 6,
  },
  previewArtCaption: {
    fontSize: 12,
  },
  previewImage: {
    width: "100%",
    height: 180,
    borderRadius: 22,
    marginBottom: 22,
    backgroundColor: colors.grey[100],
  },
  previewFileName: {
    fontSize: 18,
    lineHeight: 28,
    color: colors.grey[900],
    marginBottom: 12,
  },
  previewMetadataList: {
    gap: 8,
    marginBottom: 24,
  },
  previewMetadataRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
  },
  metaLabel: {
    fontSize: 13,
    color: colors.grey[500],
  },
  metaValue: {
    fontSize: 13,
    color: colors.grey[700],
  },
  previewSelectBtn: {
    minHeight: 44,
    borderRadius: 10,
    alignItems: "center",
    justifyContent: "center",
    paddingHorizontal: 16,
    borderColor: colors.brand[500],
    backgroundColor: colors.brand[500],
    color: colors.white,
    borderWidth: 1,
  },
  previewSelectBtnActive: {
    backgroundColor: colors.white,
    color: colors.black,
    borderWidth: 1,
    borderColor: colors.grey[200],
    shadowColor: "#101828",
    shadowOpacity: 0.08,
    shadowRadius: 10,
    shadowOffset: { width: 0, height: 4 },
  },
  previewSelectBtnText: {
    fontSize: 14,
    color: "inherit",
    fontWeight: "600",
  },
  footer: {
    minHeight: 64,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingHorizontal: 20,
    paddingVertical: 10,
    borderTopWidth: 1,
    borderTopColor: colors.grey[200],
    backgroundColor: colors.grey[50],
  },
  footerLeft: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
  },
  footerCountText: {
    fontSize: 13,
    color: colors.grey[500],
  },
  clearBtn: {
    fontSize: 13,
    color: colors.grey[500],
    textDecorationColor: colors.grey[400],
    textDecorationLine: "underline",
  },
  clearBtnHover: {
    color: colors.grey[700],
    textDecorationColor: colors.grey[700],
  },
  importBtn: {
    minHeight: 34,
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    paddingHorizontal: 16,
    borderRadius: 10,
    backgroundColor: colors.brand[500],
  },
  importBtnDisabled: {
    backgroundColor: colors.brand[200],
  },
  importBtnText: {
    fontSize: 13,
    fontWeight: "600",
    color: colors.white,
  },
});
